import {IFieldModel} from '@techsee/techsee-ui-common/lib/forms/_shared/simple-field-model';
import {TechseeFormBase} from '@techsee/techsee-ui-common/lib/forms/_shared/TechseeFormBase';
import {ValidationRules} from '@techsee/techsee-ui-common/lib/forms/_shared/ValidationRules';
import {ITranslate} from '../../services/LocalizationService';
import {IFieldFactory} from '../../app.contracts';
import {IPasswordSettings} from '../../models/AccountSettings';
import {makeHumanReadableSet} from '@techsee/techsee-common/lib/helpers/strings.helper';

export interface PasswordOptions {
    requireDigit: boolean;
    requireUppercase: boolean;
    requireSpecial: boolean;
}

export interface ChangePasswordFormValues {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

export class ChangePasswordForm extends TechseeFormBase {
    private readonly _translate: ITranslate;

    private readonly _createFieldModel: IFieldFactory;

    private readonly _passwordSettings: IPasswordSettings;

    private readonly _allowCustom: boolean;

    constructor(
        translate: ITranslate,
        createFieldModel: IFieldFactory,
        passwordSettings: IPasswordSettings,
        allowCustom: boolean
    ) {
        super();

        this._translate = translate;
        this._createFieldModel = createFieldModel;
        this._passwordSettings = passwordSettings;
        this._allowCustom = allowCustom;

        this.addField('oldPassword', this.createOldPasswordField());
        this.addField('newPassword', this.createNewPasswordField());
        this.addField('confirmPassword', this.createConfirmPasswordField());
    }

    getFormValues(): ChangePasswordFormValues {
        return {
            oldPassword: (this.fields.oldPassword?.value as string) || '',
            newPassword: (this.fields.newPassword?.value as string) || '',
            confirmPassword: (this.fields.confirmPassword?.value as string) || ''
        };
    }

    private createOldPasswordField(): IFieldModel {
        const field = this._createFieldModel('CHANGE_PASSWORD.VIEW.ENTER_OLD_PASSWORD');

        field.placeholderKey = this._translate('CHANGE_PASSWORD.VIEW.ENTER_OLD_PASSWORD');
        field.setIconName('lock');

        field.addRule({
            rule: ValidationRules.required(),
            message: this._translate('CHANGE_PASSWORD.CONTROLLER.ENTER_OLD_PASSWORD')
        });

        return field;
    }

    private createNewPasswordField(): IFieldModel {
        const field = this._createFieldModel('CHANGE_PASSWORD.VIEW.ENTER_NEW_PASSWORD');

        field.placeholderKey = this._translate('CHANGE_PASSWORD.VIEW.ENTER_NEW_PASSWORD');
        field.setIconName('lock');

        field.addRule({
            rule: ValidationRules.required(),
            message: this._translate('CHANGE_PASSWORD.CONTROLLER.ENTER_NEW_PASSWORD')
        });

        this.passwordValidationRules(field);

        return field;
    }

    private createConfirmPasswordField(): IFieldModel {
        const field = this._createFieldModel('CHANGE_PASSWORD.VIEW.CONFIRM_PASSWORD');

        field.placeholderKey = this._translate('CHANGE_PASSWORD.VIEW.CONFIRM_PASSWORD');
        field.setIconName('lock');

        field.addRule({
            rule: ValidationRules.required(),
            message: this._translate('CHANGE_PASSWORD.CONTROLLER.CONFIRM_PASSWORD')
        });

        return field;
    }

    async validateForm(): Promise<boolean> {
        const isValid = await super.validateForm();

        if (!isValid) {
            return false;
        }

        const newPassword = this.fields.newPassword?.value as string;
        const confirmPassword = this.fields.confirmPassword?.value as string;

        if (newPassword && confirmPassword && newPassword !== confirmPassword) {
            this.setFieldError('confirmPassword', this._translate('CHANGE_PASSWORD.CONTROLLER.PASSWORD_MISMATCH'));

            return false;
        }

        return true;
    }

    private passwordValidationRules(field: IFieldModel) {
        if (this._allowCustom) {
            this.createPasswordRules(field, {
                requireDigit: this._passwordSettings.requireDigit,
                requireUppercase: this._passwordSettings.requireUppercase,
                requireSpecial: this._passwordSettings.requireSpecial
            });

            if (this._passwordSettings.minLength) {
                field.addRule({
                    rule: ValidationRules.minLength(this._passwordSettings.minLength),
                    message:
                        this._translate('CHANGE_PASSWORD.CONTROLLER.MINIMUM_LENGTH') + this._passwordSettings.minLength
                });
            }
        } else {
            field.addRule({
                rule: ValidationRules.password(),
                message: this._translate('CHANGE_PASSWORD.CONTROLLER.DEFAULT_PATTERN')
            });
        }
    }

    private createPasswordRules(field: IFieldModel, options: PasswordOptions) {
        const regexps = [];
        const messages = [];
        const t = this._translate;

        if (options.requireDigit) {
            regexps.push(/(?=.*\d)/);
            messages.push(t('CHANGE_PASSWORD.CONTROLLER.CONTAIN_DIGIT'));
        }

        if (options.requireUppercase) {
            regexps.push(/(?=.*[A-Z])/);
            messages.push(t('CHANGE_PASSWORD.CONTROLLER.CONTAIN_UPPERCASE'));
        }

        if (options.requireSpecial) {
            regexps.push(/(?=.*[~`!#$€@()%\^&*+=\-\[\]\\';,\/\\{\\}\u007C\\"\u003A<>\?])/);
            messages.push(t('CHANGE_PASSWORD.CONTROLLER.CONTAIN_SPECIAL'));
        }

        if (regexps.length) {
            regexps.push(/.*$/);

            const finalRule = new RegExp(regexps.map((r) => r.source).join(''));
            const finalMessage = `${t('REACT.APP.AGENT_SETTINGS.PASSWORD_REQUIRE_PHRASE')} ${makeHumanReadableSet(
                messages,
                ',',
                t('REACT.COMMON.PHRASES.AND')
            )}`;

            field.addRule({
                rule: ValidationRules.regexp(finalRule),
                message: finalMessage
            });
        }
    }
}
